/**
 * The start screen's progress (#430, decision 112), honest and smooth.
 *
 * Three real steps end at 33, 66 and 100%. While a step runs, the bar creeps toward a cap a few
 * points under that step's value, ever more slowly (an exponential approach), so it never reaches
 * the value before the step is really done. When the step is done it eases to the value. The
 * motion is the one the Director approved on the #430 board (docs/mockups/start-screen/).
 *
 * The displayed value lives here, outside React, because the three steps are drawn by three
 * components that mount one after another (`AuthGate`, `AcceptanceGate`, `StartupGate`): each new
 * start screen continues from where the last one stopped instead of jumping back to 0.
 */
export const STEP_VALUES = [33, 66, 100] as const;
export type StartStep = 0 | 1 | 2;

/** The creep stops this many points under the running step's value. */
export const CAP_GAP = 4;
/** How quickly the creep slows, in ms. */
export const CREEP_TAU_MS = 900;
/** The eased move to a finished step's value, in ms. */
export const SETTLE_MS = 340;

let displayed = 0;
let finished = false;

export function displayedProgress(): number {
  return displayed;
}

export function setDisplayedProgress(value: number): void {
  displayed = Math.max(displayed, Math.min(100, value));
}

/** The whole start sequence has reached 100% once in this page load: it never shows again. */
export function startFinished(): boolean {
  return finished;
}

export function markStartFinished(): void {
  finished = true;
  displayed = 100;
}

/** The value a step's work starts from: the previous step's, which is done. */
export function floorOf(step: StartStep): number {
  return ([0, ...STEP_VALUES] as const)[step];
}

/** One frame of the creep: towards the cap, never past it, never backwards. */
export function creep(value: number, step: StartStep, dtMs: number): number {
  const cap = STEP_VALUES[step] - CAP_GAP;
  if (value >= cap) {
    return value;
  }
  return value + (cap - value) * (1 - Math.exp(-dtMs / CREEP_TAU_MS));
}

/** Cubic ease-out, for the move to a finished step's value. */
export function easeOut(t: number): number {
  const k = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - k, 3);
}

/**
 * Back to 0, for a start that begins again in the same page load: a sign-in after a sign-out
 * (`AuthGate`). Tests use it too.
 */
export function resetStartProgress(): void {
  displayed = 0;
  finished = false;
}
